'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Course {
  id: string;
  title: string;
  slug: string;
  category: string;
  level: string;
  price: number;
  is_free: boolean;
  is_shs: boolean;
  thumbnail_url: string | null;
  total_lessons: number;
  total_duration_minutes: number;
  total_enrollments: number;
  average_rating: number;
  total_reviews: number;
  lecturers: {
    user_profiles: {
      full_name: string;
    } | null;
  } | null;
}

const GRADIENT_MAP: Record<string, string> = {
  Design: 'from-purple-500 to-pink-500',
  Programming: 'from-green-500 to-teal-500',
  Marketing: 'from-blue-500 to-cyan-500',
  Mathematics: 'from-orange-500 to-amber-500',
  Business: 'from-red-500 to-rose-500',
  Data: 'from-violet-500 to-purple-500',
  General: 'from-indigo-500 to-blue-500',
};

function formatDuration(minutes: number): string {
  if (!minutes) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + 'm' : ''}`.trim() : `${m}m`;
}

export default function FeaturedCourses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCourses() {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('courses')
          .select(`
            id, title, slug, category, level, price, is_free, is_shs,
            thumbnail_url, total_lessons, total_duration_minutes,
            total_enrollments, average_rating, total_reviews,
            lecturers ( user_profiles ( full_name ) )
          `)
          .eq('status', 'published')
          .order('total_enrollments', { ascending: false })
          .limit(4);

        if (!error && data) {
          setCourses(data as Course[]);
        }
      } catch {
        // Silently fail — show nothing rather than crash
      } finally {
        setLoading(false);
      }
    }
    fetchCourses();
  }, []);

  if (loading) {
    return (
      <section id="courses" className="py-16 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-sm font-semibold text-primary mb-2 uppercase tracking-widest">Featured Courses</p>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-foreground">
                Learn from the <span className="text-gradient">best minds</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-card rounded-2xl border border-border shadow-card overflow-hidden animate-pulse">
                <div className="h-44 bg-muted" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-muted rounded w-1/3" />
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-6 bg-muted rounded w-1/4 mt-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (courses.length === 0) {
    return null;
  }

  return (
    <section id="courses" className="py-16 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-sm font-semibold text-primary mb-2 uppercase tracking-widest">Featured Courses</p>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-foreground">
              Learn from the{' '}
              <span className="text-gradient">best minds</span>
            </h2>
            <p className="mt-2 text-muted-foreground max-w-lg">
              Hand-picked courses taught by verified experts. Industry-relevant, certificate-backed, and built for real outcomes.
            </p>
          </div>
          <Link
            href="/sign-up-login-screen"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline flex-shrink-0"
          >
            Browse all courses
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {courses.map((course) => {
            const gradient = GRADIENT_MAP[course.category] || GRADIENT_MAP.General;
            const instructorName = course.lecturers?.user_profiles?.full_name || 'Nerdlora Instructor';
            const rating = course.average_rating || 4.8;
            const reviews = course.total_reviews || 0;

            return (
              <Link
                key={course.id}
                href="/sign-up-login-screen"
                className="group bg-card rounded-2xl border border-border shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden flex flex-col"
              >
                {/* Thumbnail */}
                <div className={`h-44 bg-gradient-to-br ${gradient} relative flex items-center justify-center overflow-hidden`}>
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon name="BookOpenIcon" size={48} className="text-white/30" />
                  )}
                  <div className="absolute top-3 left-3 flex gap-2">
                    {course.is_free ? (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-success text-white">Free</span>
                    ) : (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-warning text-white">Paid</span>
                    )}
                    {course.is_shs && (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-green-600 text-white">SHS</span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-primary bg-secondary px-2 py-0.5 rounded-full">{course.category}</span>
                    <span className="text-xs text-muted-foreground">{course.level}</span>
                  </div>

                  <h3 className="font-bold text-foreground text-sm leading-snug mb-1 group-hover:text-primary transition-colors line-clamp-2">
                    {course.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">by {instructorName}</p>

                  <div className="flex items-center gap-1 mb-2">
                    <span className="text-xs font-bold text-warning">{rating.toFixed(1)}</span>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Icon
                          key={`${course.id}-star-${s}`}
                          name="StarIcon"
                          size={11}
                          className={s <= Math.floor(rating) ? 'text-warning' : 'text-muted'}
                          variant="solid"
                        />
                      ))}
                    </div>
                    {reviews > 0 && (
                      <span className="text-xs text-muted-foreground">({reviews.toLocaleString()})</span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Icon name="ClockIcon" size={12} />
                      {formatDuration(course.total_duration_minutes)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="PlayCircleIcon" size={12} />
                      {course.total_lessons} lessons
                    </span>
                  </div>

                  <div className="mt-auto flex items-center justify-between">
                    {course.is_free ? (
                      <span className="text-base font-extrabold text-success">Free</span>
                    ) : (
                      <span className="text-base font-extrabold text-foreground tabular-nums">GHS {course.price}</span>
                    )}
                    {course.total_enrollments > 0 && (
                      <span className="text-xs text-muted-foreground">{course.total_enrollments.toLocaleString()} students</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}